import { useState } from 'react';
import { useQuiz } from '../context/QuizContext';
import { fetchPerguntasByFase } from '../services/quizFirebaseService';
import { Lock, Check, Play, Loader2, AlertCircle, Map, ChevronLeft, ChevronRight, Star, Globe } from 'lucide-react';
import { useGameAudio } from '../hooks/useGameAudio';
import { GameShell } from '../components/layout/GameShell';
import { CharacterPortrait } from '../components/CharacterPortrait/CharacterPortrait';
import { CICLOS } from '../utils/quizUtils';

// Abas derivadas da fonte única de quizUtils: III 21–35, IV 36–49, V só a 50.
const CICLOS_UI = CICLOS.map((c, idx) => {
  const [ini, fim] = c.fases;
  const [num, ...resto] = c.nome.split('. ');
  return {
    id: idx + 1,
    nome: `Ciclo ${num}`,
    titulo: resto.join('. '),
    fases: Array.from({ length: fim - ini + 1 }, (_, i) => ini + i),
  };
});

export function FaseSelectScreen() {
  const { state, dispatch } = useQuiz();
  const { playHover, playClick, playSuccess, playError } = useGameAudio();
  const [loadingFase, setLoadingFase] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fasesConcluidas = state.fasesConcluidas;

  // Determinar a última fase desbloqueada
  const ultimaDesbloqueada = Math.min(50, Math.max(1, ...fasesConcluidas.map((f) => f + 1)));
  const cicloInicialIndex = Math.max(0, CICLOS_UI.findIndex((c) => c.fases.includes(ultimaDesbloqueada)));
  const [cicloAtivoIndex, setCicloAtivoIndex] = useState<number>(cicloInicialIndex);

  const isDesbloqueada = (fase: number) => fase === 1 || fasesConcluidas.includes(fase - 1);
  const isConcluida = (fase: number) => fasesConcluidas.includes(fase);

  const cicloAtual = CICLOS_UI[cicloAtivoIndex];

  // O botão primário segue o ciclo NAVEGADO: joga a última fase desbloqueada
  // dentro dele; se o ciclo ainda está trancado, cai na última global.
  const faseAlvo = [...cicloAtual.fases].reverse().find(isDesbloqueada) ?? ultimaDesbloqueada;

  const handleIniciarFase = async (fase: number) => {
    try {
      playSuccess();
      setLoadingFase(fase);
      setErrorMsg(null);
      const perguntas = await fetchPerguntasByFase(fase);
      dispatch({ type: 'START_PHASE', payload: { perguntas, fase } });
    } catch (e: any) {
      console.error(e);
      playError();
      setErrorMsg('Não conseguimos conexão com os Imortais. Os portões do Axé se trancaram. Verifique sua rede.');
    } finally {
      setLoadingFase(null);
    }
  };

  return (
    <GameShell maxWidth="2xl" maxHeightCeiling="max-h-[100dvh]">
      <div className="w-full h-full flex-1 flex flex-col justify-between p-3 sm:p-4 animate-in fade-in duration-300 relative z-10">

        {/* Cabeçalho de JOGO (não app-bar web): controles numa linha, título como
            emblema centralizado e uma barra de conquista com glow no lugar do
            parágrafo. Zero prosa que quebra linha. */}
        <div className="glass-panel px-3 sm:px-4 py-2 rounded-xl border-sky-500/30 flex flex-col gap-2 bg-slate-900/80 shadow-neon-cyan shrink-0 relative">
          {/* Linha de controles: sair para o Winiverso à esquerda, título centralizado e conquista como STAT à direita */}
          <div className="flex items-center justify-between gap-2 w-full">
            <a
              href="https://www.winiverso.com/"
              className="px-2.5 py-1 rounded-lg bg-slate-800/80 text-slate-200 hover:text-white hover:bg-sky-500/20 transition border border-slate-700 hover:border-sky-400 shrink-0 text-fluid-xs font-cyber font-semibold flex items-center gap-1.5 shadow-sm"
              title="Sair para o portal Winiverso"
            >
              <Globe size={13} className="text-sky-400 shrink-0" />
              <span>Sair</span>
            </a>

            {/* Emblema central: ícone + wordmark com gradiente e glow */}
            <div className="flex items-center justify-center gap-1.5 min-w-0 flex-1 px-1">
              <Map size={15} className="text-sky-400 shrink-0 drop-shadow-[0_0_6px_rgba(56,189,248,0.7)] hidden xs:block" />
              <h1 className="font-cyber font-black tracking-wider text-xs sm:text-fluid-sm uppercase truncate text-transparent bg-clip-text bg-gradient-to-b from-sky-100 to-sky-500 drop-shadow-[0_0_6px_rgba(56,189,248,0.35)]">
                Odisseia de Fases
              </h1>
            </div>

            <div className="shrink-0 flex items-baseline gap-0.5 font-cyber leading-none">
              <span className="text-fluid-subtitle font-black text-sky-300 drop-shadow-[0_0_5px_rgba(56,189,248,0.5)]">{fasesConcluidas.length}</span>
              <span className="text-fluid-xs font-bold text-slate-500">/50</span>
            </div>
          </div>

          {/* Barra de conquista da Odisseia — elemento de jogo, com trilha
              entalhada e preenchimento em glow proporcional às fases vencidas. */}
          <div
            className="h-1.5 rounded-full bg-slate-950/90 border border-slate-700/60 overflow-hidden"
            role="progressbar"
            aria-valuenow={fasesConcluidas.length}
            aria-valuemin={0}
            aria-valuemax={50}
            aria-label="Fases concluídas"
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-sky-500 via-sky-400 to-emerald-400 shadow-[0_0_8px_rgba(56,189,248,0.8)] transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(2, (fasesConcluidas.length / 50) * 100)}%` }}
            />
          </div>
        </div>

        {errorMsg && (
          <div className="my-1.5 bg-red-900/30 border-l-4 border-l-red-500 rounded-r-xl p-2 flex items-center gap-3 shrink-0">
            <AlertCircle className="shrink-0 text-red-500" size={16} />
            <p className="text-red-100 leading-snug font-medium text-xs">{errorMsg}</p>
          </div>
        )}

        {/* Módulo coeso: seletor de ciclos + grid de fases são a MESMA tarefa
            (navegar e escolher fase), então vivem num único bloco centralizado.
            A folga da tela vira moldura simétrica externa, não vão interno. */}
        <div className="flex-1 min-h-0 flex flex-col justify-center relative">
          <div className="stage-module w-full">

            {/* Seletor de Ciclos por Abas / Setas */}
            <div className="glass-panel p-2 rounded-xl border-slate-700/50 flex items-center justify-between shrink-0 bg-slate-950/60">
              <button
                onMouseEnter={playHover}
                onClick={() => { playClick(); setCicloAtivoIndex((prev) => Math.max(0, prev - 1)); }}
                disabled={cicloAtivoIndex === 0}
                className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1.5">
                  {CICLOS_UI.map((c, idx) => (
                    <button
                      key={c.id}
                      onClick={() => { playClick(); setCicloAtivoIndex(idx); }}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        idx === cicloAtivoIndex
                          ? 'bg-amber-400 w-6 shadow-[0_0_8px_#f59e0b]'
                          : 'bg-slate-700 hover:bg-slate-500'
                      }`}
                      title={c.nome}
                    />
                  ))}
                </div>
                <span className="font-cyber font-bold text-xs sm:text-sm text-amber-400 uppercase tracking-widest mt-1">
                  {cicloAtual.nome}: {cicloAtual.titulo}
                </span>
              </div>

              <button
                onMouseEnter={playHover}
                onClick={() => { playClick(); setCicloAtivoIndex((prev) => Math.min(CICLOS_UI.length - 1, prev + 1)); }}
                disabled={cicloAtivoIndex === CICLOS_UI.length - 1}
                className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Grid de fases do ciclo (5 por linha; III tem 3 linhas, V tem 1).
                Card com altura fluida clamp(76px,12dvh,112px): em 360×640 fica
                no piso (12dvh=76,8px ≈ hoje); telas altas ganham o retrato maior. */}
            <div className="relative">
              {loadingFase !== null && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-slate-950/80 backdrop-blur-md rounded-2xl border border-amber-500/40 shadow-neon-amber">
                  <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-2" />
                  <p className="font-cyber font-bold tracking-widest text-amber-400 animate-pulse text-xs">Sincronizando Sabedoria...</p>
                </div>
              )}

              <div className="grid grid-cols-5 gap-2 sm:gap-2.5 p-1">
                {cicloAtual.fases.map((fase) => {
                  const desbloqueada = isDesbloqueada(fase);
                  const concluida = isConcluida(fase);
                  const rodando = loadingFase === fase;

                  return (
                    <button
                      key={fase}
                      onMouseEnter={desbloqueada && !loadingFase ? playHover : undefined}
                      onClick={() => { if (desbloqueada && !loadingFase) { playClick(); handleIniciarFase(fase); } }}
                      disabled={!desbloqueada || loadingFase !== null}
                      className={`
                        relative flex flex-col items-center justify-between py-1.5 px-1 sm:px-1.5 rounded-xl font-cyber font-bold transition-all duration-300 h-[clamp(76px,12dvh,112px)]
                        ${desbloqueada
                          ? 'glass-panel border-sky-500/40 text-sky-100 hover:text-white hover:border-sky-400 hover:shadow-neon-cyan hover:-translate-y-0.5 hover:bg-slate-800'
                          : 'bg-slate-900/80 border border-slate-800/80 cursor-not-allowed text-slate-600 opacity-50'}
                        ${concluida ? 'border-emerald-500/40 shadow-[inset_0_0_12px_rgba(16,185,129,0.2)] bg-slate-800/90 text-emerald-100' : ''}
                      `}
                    >
                      {/* Identidade: retrato do Imortal da fase (GD1). Fase trancada
                          não revela o retrato; fase 50 mostra o emblema Tribunal (GD2). */}
                      <div className="flex items-center justify-center pointer-events-none">
                        {desbloqueada ? (
                          rodando
                            ? <Loader2 className="text-amber-400 w-5 h-5 animate-spin" />
                            : <CharacterPortrait fase={fase} variant="compact" showLabel={false} />
                        ) : (
                          <Lock className="text-slate-600 w-3.5 h-3.5 mt-1.5" />
                        )}
                      </div>

                      {/* Estado no canto (não compete com o retrato) */}
                      {desbloqueada && !rodando && (
                        <span className="absolute top-0.5 right-0.5 pointer-events-none">
                          {concluida
                            ? <Check className="text-emerald-400 w-3.5 h-3.5 drop-shadow-[0_0_5px_rgba(16,185,129,0.5)]" />
                            : <Play className="text-amber-400 w-3 h-3 opacity-80 drop-shadow-[0_0_5px_rgba(245,158,11,0.5)]" fill="currentColor" />}
                        </span>
                      )}

                      {/* Número da Fase */}
                      <span className={`text-sm sm:text-base font-black leading-none ${desbloqueada ? '' : 'opacity-60'} ${concluida ? 'text-emerald-400' : ''}`}>{fase}</span>

                      {/* Rating Estrelas / Status */}
                      <div className="flex items-center gap-0.5">
                        {concluida ? (
                          <div className="flex gap-0.5 text-amber-400">
                            <Star size={8} fill="currentColor" />
                            <Star size={8} fill="currentColor" />
                            <Star size={8} fill="currentColor" />
                          </div>
                        ) : desbloqueada ? (
                          <span className="text-[8px] text-sky-400 uppercase tracking-widest">Ativo</span>
                        ) : (
                          <span className="text-[8px] text-slate-600 uppercase tracking-widest">Trancado</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Zona do Polegar: joga a fase ativa DO CICLO NAVEGADO */}
        <div className="shrink-0 mt-2">
          <button
            onMouseEnter={playHover}
            onClick={() => { playClick(); handleIniciarFase(faseAlvo); }}
            disabled={loadingFase !== null}
            className="btn-cyber w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-black tracking-wider uppercase text-fluid-body border border-amber-300 shadow-neon-amber hover:bg-amber-400 transition-all flex items-center justify-center gap-2"
          >
            <Play size={16} fill="currentColor" />
            Jogar Fase {faseAlvo}{faseAlvo === ultimaDesbloqueada ? ' (Fase Atual)' : ''}
          </button>
        </div>

      </div>
    </GameShell>
  );
}
